import { ReactNode, useEffect, useState } from 'react';
import { Address, getAddress } from 'viem';
import { ZERO_ADDRESS } from '~/core/constants';
import { api, BlinkoProfile } from '../api';

interface ProfileSlotProps {
  address?: Address;
  children: ({ profile, onLink }: {
    profile: BlinkoProfile,
    onLink: () => void,
  }) => ReactNode;
}

const createEmptyProfile = (address: Address): BlinkoProfile => ({
  address,
  name: `blinko${address.slice(2, 6)}`,
  picture: null,
  twitterId: null,
  twitterUsername: null,
  twitterHandle: null,
  harvestedHoney: '0',
  referralHoney: '0',
  totalHoney: '0',
  profitShareBps: null,
  profitShareUntil: null,
  profitShareEarnings: '0',
  settledProfitShare: '0',
  pendingProfitShare: '0',
  referralCode: null,
  referredByAddress: null,
  usedReferralCode: null,
  restrictedUntil: null,
  createdAt: Date.now() / 1000,
  stats: undefined,
});

class ProfileCache {
  private cache: { [key: Address]: Promise<BlinkoProfile> } = {};
  private listeners: Array<(address: Address) => void> = [];

  get(address: Address): Promise<BlinkoProfile> {
    if (!this.cache[address]) {
      this.update(address);
    }
    return this.cache[address]!;
  }

  set(address: Address, profile: BlinkoProfile) {
    this.cache[address] = Promise.resolve(profile);
    this.emit(address);
  }

  update(address: Address) {
    this.cache[address] = this.fetchProfile(address);
  }

  debouncedUpdate(address: Address) {
    this.update(address);
  }

  delete(address: Address) {
    delete this.cache[address];
  }

  mutate(address: Address) {
    this.delete(address);
    this.emit(address);
  }

  subscribe(listener: (address: Address) => void): () => void {
    this.listeners.push(listener);

    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    }
  }

  private emit(address: Address) {
    this.listeners.forEach(listener => listener(address));
  }

  private async fetchProfile(address: Address) {
    const promise = api.getProfile(address)
      .then(profile => ({
        ...profile,
        name: profile.name || `blinko${address.slice(2, 6)}`,
      }))
      .catch((error) => {
        this.delete(address);
        throw error;
      });
    this.cache[address] = promise;
    return promise;
  }
}

export const profileCache = new ProfileCache();

export const ProfileSlot: React.FC<ProfileSlotProps> = ({ address = ZERO_ADDRESS as Address, children }) => {
  const [profile, setProfile] = useState<BlinkoProfile>(createEmptyProfile(address));

  useEffect(() => {
    if (address !== ZERO_ADDRESS) {
      fetchProfile();
    }
  }, [address]);

  useEffect(() => {
    const unsubscribe = profileCache.subscribe((changedAddress) => {
      if (changedAddress === address) {
        fetchProfile();
      }
    });
    return () => unsubscribe();
  }, [address]);

  const fetchProfile = async () => {
    const checksumAddress = getAddress(address);

    if (!profileCache.get(checksumAddress)) {
      profileCache.debouncedUpdate(checksumAddress);
    }

    try {
      const userProfile = await profileCache.get(checksumAddress)!;
      setProfile(userProfile);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      profileCache.delete(checksumAddress);
      setProfile(createEmptyProfile(checksumAddress));
    }
  };

  const onLink = () => {
    const handle = profile.twitterHandle || profile.twitterUsername || profile.name;
    if (handle) {
      const sanitizedHandle = handle.startsWith('@') ? handle.slice(1) : handle;
      window.open(`https://x.com/${sanitizedHandle}`, '_blank');
    }
  };

  return (
    <>
      {children({ profile, onLink })}
    </>
  );
};
